import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { Scooter, ActivePack, ScooterUsage } from '../../services/api/types';
import { scooterService, packService } from '../../services/api';

interface ScooterState {
  // Multi-scooter management
  scooters: Scooter[];
  selectedScooterVin: string | null;
  
  // Loading states
  loading: {
    scooters: boolean;
    activePack: boolean;
    usage: boolean;
    switching: boolean;
  };
  
  // Current scooter data
  currentScooter: Scooter | null;
  activePack: ActivePack | null;
  queuedPacks: ActivePack[];
  usage: ScooterUsage | null;
  
  // Cache for quick switching
  scooterDataCache: Record<string, {
    scooter: Scooter;
    activePack: ActivePack | null;
    queuedPacks: ActivePack[];
    usage: ScooterUsage | null;
    lastUpdated: number;
  }>;
  evictedVins: Record<string, boolean>;
  
  // Error handling
  error: string | null;
  
  // UI state
  showScooterSelector: boolean;
  lastDataRefresh: number;
}

const initialState: ScooterState = {
  scooters: [],
  selectedScooterVin: null,
  loading: {
    scooters: false,
    activePack: false,
    usage: false,
    switching: false,
  },
  currentScooter: null,
  activePack: null,
  queuedPacks: [],
  usage: null,
  scooterDataCache: {},
  evictedVins: {},
  error: null,
  showScooterSelector: false,
  lastDataRefresh: 0,
};

// Async thunks
export const fetchUserScooters = createAsyncThunk(
  'scooter/fetchUserScooters',
  async (_, { rejectWithValue }) => {
    try {
      const scooters = await scooterService.getUserScooters();
      return scooters;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const switchToScooter = createAsyncThunk(
  'scooter/switchToScooter',
  async (vin: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { scooter: ScooterState };
      const cache = state.scooter.scooterDataCache[vin];
      
      // Check if we have recent cached data (less than 15 minutes old)
      const cacheValid = cache && (Date.now() - cache.lastUpdated) < 15 * 60 * 1000;
      
      if (cacheValid) {
        return {
          vin,
          cached: true,
          data: cache,
        };
      }
      
      // Fetch fresh data
      console.log('🔄 ScooterSlice: Fetching fresh data for VIN:', vin);
      const [scooter, packData, usage] = await Promise.all([
        scooterService.getScooterByVin(vin),
        packService.getScooterPackData(vin),
        scooterService.getScooterUsage(vin),
      ]);
      
      const { activePack, queuedPacks } = packData;
      
      console.log('🔄 ScooterSlice: Fresh data fetched:', { scooter, activePack, queuedPacks, usage });
      
      return {
        vin,
        cached: false,
        data: {
          scooter,
          activePack,
          queuedPacks,
          usage,
          lastUpdated: Date.now(),
        },
      };
    } catch (error: any) {
      const status = error?.response?.status;
      return rejectWithValue({ message: error?.message || 'Unable to load scooter', vin, evict: status === 403 });
    }
  }
);

export const refreshCurrentScooterData = createAsyncThunk(
  'scooter/refreshCurrentScooterData',
  async (_, { getState, rejectWithValue }) => {
    const { selectedScooterVin } = (getState() as { scooter: ScooterState }).scooter;
    try {
      
      if (!selectedScooterVin) {
        throw new Error('No scooter selected');
      }
      
      // Force refresh data
      const [packData, usage] = await Promise.all([
        packService.getScooterPackData(selectedScooterVin),
        scooterService.getScooterUsage(selectedScooterVin),
      ]);
      
      const { activePack, queuedPacks } = packData;
      
      return {
        vin: selectedScooterVin,
        activePack,
        queuedPacks,
        usage,
        lastUpdated: Date.now(),
      };
    } catch (error: any) {
      const status = error?.response?.status;
      return rejectWithValue({ message: error?.message || 'Unable to refresh scooter', vin: selectedScooterVin || undefined, evict: status === 403 });
    }
  }
);

export const updateScooterPreferences = createAsyncThunk(
  'scooter/updateScooterPreferences',
  async (
    { vin, preferences }: { vin: string; preferences: { nickname?: string; color_code?: string } },
    { rejectWithValue }
  ) => {
    try {
      const updatedScooter = await scooterService.updateScooterPreferences(vin, preferences);
      return { vin, scooter: updatedScooter };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const scooterSlice = createSlice({
  name: 'scooter',
  initialState,
  reducers: {
    setSelectedScooter: (state, action: PayloadAction<string>) => {
      state.selectedScooterVin = action.payload;
      state.currentScooter = state.scooters.find(s => s.vin === action.payload) || null;
      state.activePack = null;
      state.queuedPacks = [];
      state.usage = null;
    },
    
    clearScooterData: (state) => {
      state.currentScooter = null;
      state.activePack = null;
      state.queuedPacks = [];
      state.usage = null;
      state.selectedScooterVin = null;
    },

    // Ownership changes can remove a VIN from the rider account. Evict all
    // cached pack/usage state before the next account refetch.
    evictScooter: (state, action: PayloadAction<string>) => {
      const vin = action.payload;
      state.evictedVins[vin] = true;
      delete state.scooterDataCache[vin];
      state.scooters = state.scooters.filter(scooter => scooter.vin !== vin);
      if (state.selectedScooterVin === vin) {
        state.selectedScooterVin = null;
        state.currentScooter = null;
        state.activePack = null;
        state.queuedPacks = [];
        state.usage = null;
      }
    },
    
    toggleScooterSelector: (state) => {
      state.showScooterSelector = !state.showScooterSelector;
    },
    
    closeScooterSelector: (state) => {
      state.showScooterSelector = false;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    // Update active pack (e.g., after purchase)
    updateActivePack: (state, action: PayloadAction<ActivePack | null>) => {
      state.activePack = action.payload;
      
      // Update cache if we have selected scooter
      if (state.selectedScooterVin && state.scooterDataCache[state.selectedScooterVin]) {
        state.scooterDataCache[state.selectedScooterVin].activePack = action.payload;
        state.scooterDataCache[state.selectedScooterVin].lastUpdated = Date.now();
      }
    },
    
    // Add new pack to queue
    addToPackQueue: (state, action: PayloadAction<ActivePack>) => {
      state.queuedPacks.push(action.payload);
      
      // Update cache
      if (state.selectedScooterVin && state.scooterDataCache[state.selectedScooterVin]) {
        state.scooterDataCache[state.selectedScooterVin].queuedPacks.push(action.payload);
        state.scooterDataCache[state.selectedScooterVin].lastUpdated = Date.now();
      }
    },
    
    // Set default scooter (first scooter if none selected)
    setDefaultScooter: (state) => {
      if (!state.selectedScooterVin && state.scooters.length > 0) {
        state.selectedScooterVin = state.scooters[0].vin;
        state.currentScooter = state.scooters[0];
      }
    },
    
    // Clear old cache entries (older than 1 hour)
    cleanCache: (state) => {
      const oneHourAgo = Date.now() - 60 * 60 * 1000;
      Object.keys(state.scooterDataCache).forEach(vin => {
        if (state.scooterDataCache[vin].lastUpdated < oneHourAgo) {
          delete state.scooterDataCache[vin];
        }
      });
    },
  },
  
  extraReducers: (builder) => {
    // Fetch user scooters
    builder
      .addCase(fetchUserScooters.pending, (state) => {
        state.loading.scooters = true;
        state.error = null;
      })
      .addCase(fetchUserScooters.fulfilled, (state, action) => {
        state.loading.scooters = false;
        const currentVins = new Set(action.payload.map(scooter => scooter.vin));
        state.scooters.forEach(scooter => {
          if (!currentVins.has(scooter.vin)) state.evictedVins[scooter.vin] = true;
        });
        state.scooters = action.payload;
        Object.keys(state.evictedVins).forEach(vin => {
          if (currentVins.has(vin)) delete state.evictedVins[vin];
        });
        Object.keys(state.scooterDataCache).forEach(vin => {
          if (!currentVins.has(vin)) {
            state.evictedVins[vin] = true;
            delete state.scooterDataCache[vin];
          }
        });
        
        // Update current scooter if it's in the new list
        if (state.selectedScooterVin) {
          const updatedCurrentScooter = action.payload.find(s => s.vin === state.selectedScooterVin);
          if (updatedCurrentScooter) {
            state.currentScooter = updatedCurrentScooter;
          } else {
            state.evictedVins[state.selectedScooterVin] = true;
            state.selectedScooterVin = null;
            state.currentScooter = null;
            state.activePack = null;
            state.queuedPacks = [];
            state.usage = null;
          }
        }
        // Note: Default scooter selection is handled by route components so
        // each surface can choose its own data-loading path.
      })
      .addCase(fetchUserScooters.rejected, (state, action) => {
        state.loading.scooters = false;
        state.error = action.payload as string;
      });
    
    // Switch to scooter
    builder
      .addCase(switchToScooter.pending, (state) => {
        state.loading.switching = true;
        state.error = null;
      })
      .addCase(switchToScooter.fulfilled, (state, action) => {
        state.loading.switching = false;
        const { vin, data } = action.payload;
        if (state.evictedVins[vin]) return;
        
        // Update current scooter data
        state.selectedScooterVin = vin;
        state.currentScooter = data.scooter;
        state.activePack = data.activePack;
        state.queuedPacks = data.queuedPacks;
        state.usage = data.usage;
        
        // Update cache
        state.scooterDataCache[vin] = data;
        state.lastDataRefresh = Date.now();
        
        // Close selector
        state.showScooterSelector = false;
      })
      .addCase(switchToScooter.rejected, (state, action) => {
        state.loading.switching = false;
        const payload = action.payload as { message?: string; vin?: string; evict?: boolean } | undefined;
        if (payload?.evict && payload.vin) {
          state.evictedVins[payload.vin] = true;
          delete state.scooterDataCache[payload.vin];
          state.scooters = state.scooters.filter(scooter => scooter.vin !== payload.vin);
          if (state.selectedScooterVin === payload.vin) {
            state.selectedScooterVin = null;
            state.currentScooter = null;
            state.activePack = null;
            state.queuedPacks = [];
            state.usage = null;
          }
        }
        state.error = payload?.message || action.payload as string;
      });
    
    // Refresh current scooter data
    builder
      .addCase(refreshCurrentScooterData.pending, (state) => {
        state.loading.activePack = true;
        state.loading.usage = true;
      })
      .addCase(refreshCurrentScooterData.fulfilled, (state, action) => {
        state.loading.activePack = false;
        state.loading.usage = false;
        
        const { vin, activePack, queuedPacks, usage, lastUpdated } = action.payload;
        if (state.evictedVins[vin] || state.selectedScooterVin !== vin) return;
        
        // Update current data
        console.log('🔧 ScooterSlice: Updating activePack in Redux:', activePack);
        state.activePack = activePack;
        state.queuedPacks = queuedPacks;
        state.usage = usage;
        state.lastDataRefresh = lastUpdated;
        
        // Update cache
        if (state.scooterDataCache[vin]) {
          state.scooterDataCache[vin].activePack = activePack;
          state.scooterDataCache[vin].queuedPacks = queuedPacks;
          state.scooterDataCache[vin].usage = usage;
          state.scooterDataCache[vin].lastUpdated = lastUpdated;
        }
      })
      .addCase(refreshCurrentScooterData.rejected, (state, action) => {
        state.loading.activePack = false;
        state.loading.usage = false;
        const payload = action.payload as { message?: string; vin?: string; evict?: boolean } | undefined;
        if (payload?.evict && payload.vin) {
          state.evictedVins[payload.vin] = true;
          delete state.scooterDataCache[payload.vin];
          state.scooters = state.scooters.filter(scooter => scooter.vin !== payload.vin);
          if (state.selectedScooterVin === payload.vin) {
            state.selectedScooterVin = null;
            state.currentScooter = null;
            state.activePack = null;
            state.queuedPacks = [];
            state.usage = null;
          }
        }
        state.error = payload?.message || action.payload as string;
      });
    
    // Update scooter preferences
    builder
      .addCase(updateScooterPreferences.fulfilled, (state, action) => {
        const { vin, scooter } = action.payload;
        
        // Update in scooters array
        const index = state.scooters.findIndex(s => s.vin === vin);
        if (index !== -1) {
          state.scooters[index] = scooter;
        }
        
        // Update current scooter if it's the selected one
        if (state.selectedScooterVin === vin) {
          state.currentScooter = scooter;
        }
        
        // Update cache
        if (state.scooterDataCache[vin]) {
          state.scooterDataCache[vin].scooter = scooter;
        }
      });
  },
});

export const {
  setSelectedScooter,
  clearScooterData,
  toggleScooterSelector,
  closeScooterSelector,
  clearError,
  evictScooter,
  updateActivePack,
  addToPackQueue,
  setDefaultScooter,
  cleanCache,
} = scooterSlice.actions;

export default scooterSlice.reducer;

// Selectors
export const selectScooters = (state: { scooter: ScooterState }) => state.scooter.scooters;
export const selectCurrentScooter = (state: { scooter: ScooterState }) => state.scooter.currentScooter;
export const selectSelectedScooterVin = (state: { scooter: ScooterState }) => state.scooter.selectedScooterVin;
export const selectActivePack = (state: { scooter: ScooterState }) => state.scooter.activePack;
export const selectQueuedPacks = (state: { scooter: ScooterState }) => state.scooter.queuedPacks;
export const selectScooterUsage = (state: { scooter: ScooterState }) => state.scooter.usage;
export const selectScooterLoading = (state: { scooter: ScooterState }) => state.scooter.loading;
export const selectScooterError = (state: { scooter: ScooterState }) => state.scooter.error;
export const selectShowScooterSelector = (state: { scooter: ScooterState }) => state.scooter.showScooterSelector;
export const selectLastDataRefresh = (state: { scooter: ScooterState }) => state.scooter.lastDataRefresh;
